/**
 * Pricing — Egnite (free) + Enferno (paid) + Founder's tier + Hero deferred.
 * Built from the founder's pricing sheet with the edits applied:
 * perimenopause moved to Coming, community challenges removed, mobility
 * "flows" reworded, 8-arts claim softened, Hero tier deferred.
 *
 * Architecture: /pricing is the single "action layer" for
 * all plan changes. Profile's PlanAndBillingBlock routes EVERY user
 * here for upgrade, downgrade, cancel, billing portal, promo discovery.
 *
 * Subscribe button hits /api/payments/checkout/subscription and redirects
 * to Stripe Checkout. After purchase, Stripe sends user back to
 * /payments/return which polls /api/payments/checkout/status/:id to
 * detect entitlement grant.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { openStripeCheckout } from '../lib/stripeCheckout';
import {
  DEV_TOPUP_PICKER_CHANGED,
  isDevTopupPickerEnabled,
} from '../lib/devTopupPicker';
import { Check, ArrowLeft, X, Loader2, Heart, Lock, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DonationModal from '../components/DonationModal';

import FounderUpgradeDialog from '../components/FounderUpgradeDialog';
import MissionTopupCheckoutDialog from '../components/MissionTopupCheckoutDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'enferno-monthly',
    label: 'Monthly',
    price: '$9.99',
    cadence: '/mo',
    note: '14-day free trial · cancel anytime',
  },
  {
    id: 'enferno-annual',
    label: 'Annual',
    price: '$69.99',
    cadence: '/yr',
    note: 'Save 42% vs monthly · 14-day free trial',
    highlight: true,
  },
];

const EGNITE_PERKS = [
  '4 of the 5 Enferno pillars: Strength, Endurance, Combatives, Adaptive (Nutrition is Enferno-only)',
  '80 baseline exercises across pillars',
  'Daily check-ins, pain score, adaptive recommendations',
  'Adaptive Mode + IronLimb depth tools (Care Team PDF, Residual-Limb Log, Adaptive Gear, Adaptive Lens)',
  'Impact Dashboard',
  'Donate to IronLimb anytime',
];

// G6: honest, anti-shame disclosure of what Egnite users do
// NOT get. Surfaced as a separate "What you're missing" section on the
// Egnite card so users can compare apples-to-apples without re-reading
// the Enferno column.
const EGNITE_LIMITS = [
  '30-day rolling training history (Enferno: forever)',
  'Combatives basic session logs only — no detailed journals',
  '3 photo/video attachments per session (Enferno: unlimited)',
  'No YouTube / Vimeo reference links on sessions',
  'No journal CSV export',
  'No AI workout generation or Coach chat',
];

const ENFERNO_PERKS = [
  'Full movement library (300+ exercises) + custom + saved',
  'Unlimited training history',
  'AI workout generation (monthly limit)',
  'Ask Enferno coach chat (monthly limit)',
  'Nutrition tracking + macro logging + custom foods',
  'Combatives detailed journals — BJJ, Muay Thai, Judo, Wrestling, Boxing',
  'Mobility & rehab programming',
  'GPS-tracked endurance + transitive wearables (Garmin / Whoop / Oura / Fitbit)',
  'Apple Health + Health Connect sync at launch',
  'Wellbeing trends + advanced charts',
  '10% off Enferno apparel',
  'Priority support (support.vip@enferno.app)',
  'Cycle-aware programming — coming post-launch',
  'IronLimb Recovery Suite — coming post-launch',
];

const COMPETITORS = [
  { name: 'Enferno', price: '$69.99/yr', highlight: true },
  { name: 'Strava Premium', price: '$79.99/yr' },
  { name: 'MyFitnessPal Premium', price: '$79.99/yr' },
  { name: 'Apple Fitness+', price: '$79.99/yr' },
  { name: 'Fitbod', price: '$95.99/yr' },
];

const Pricing = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [donateOpen, setDonateOpen] = useState(false);
  const [params] = useSearchParams();
  const canceled = params.get('canceled') === '1';
  const [founderSeats, setFounderSeats] = useState(null);
  const [founderModalOpen, setFounderModalOpen] = useState(false);
  // Current plan state for the signed-in user. Drives the "Your current
  // plan" indicator on each tier card + the Manage-billing button +
  // the "step away" soft text for lifetime users.
  const [mySub, setMySub] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [devTopupPicker, setDevTopupPicker] = useState(isDevTopupPickerEnabled);
  const [topupModalPlan, setTopupModalPlan] = useState(null);

  useEffect(() => {
    const sync = () => setDevTopupPicker(isDevTopupPickerEnabled());
    window.addEventListener(DEV_TOPUP_PICKER_CHANGED, sync);
    return () => window.removeEventListener(DEV_TOPUP_PICKER_CHANGED, sync);
  }, []);

  // Fetch live Founder seat count on mount. Falls back to "limited" if the
  // endpoint is unreachable so the page never blocks on this.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/payments/founder/seats`)
      .then((res) => { if (!cancelled) setFounderSeats(res.data); })
      .catch(() => { /* non-fatal — UI gracefully degrades */ });
    if (user) {
      axios.get(`${API}/payments/me`)
        .then((res) => { if (!cancelled) setMySub(res.data); })
        .catch(() => { /* non-fatal */ });
    }
    return () => { cancelled = true; };
  }, [user]);

  // Derived plan state. Lifetime trumps stripe status.
  const myTier = mySub?.subscription_tier || 'egnite';
  const myStatus = mySub?.subscription_status;
  const myLifetime = mySub?.lifetime === true || ['founder', 'hero', 'comp'].includes(myTier);
  const hasActivePaidSub = myTier === 'enferno' && ['active', 'trialing', 'past_due'].includes(myStatus);

  // Manage billing → Stripe Portal. Visible ONLY for active paid subs;
  // lifetime users have no billing to manage, free users have nothing
  // to manage. Returns user to /pricing on portal exit.
  const openPortal = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/payments/portal`, { origin_url: window.location.origin });
      if (res.data?.url) {
        await openStripeCheckout(res.data.url);
      } else {
        setPortalError('Could not open billing portal. Try again.');
        setPortalLoading(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPortalError(typeof detail === 'string' ? detail : 'Could not open billing portal. Try again.');
      setPortalLoading(false);
    }
  };

  const startSubscribe = (planId) => {
    if (!user) {
      window.location.href = `/login?return=/pricing`;
      return;
    }
    if (devTopupPicker) {
      const plan = PLANS.find((p) => p.id === planId);
      setTopupModalPlan(plan || { id: planId, label: planId, price: '', cadence: '' });
      return;
    }
    subscribe(planId);
  };

  const subscribe = async (planId, missionTopupId = null) => {
    if (!user) {
      window.location.href = `/login?return=/pricing`;
      return;
    }
    setLoading(planId);
    try {
      const body = {
        plan_id: planId,
        origin_url: window.location.origin,
      };
      if (devTopupPicker) {
        body.mission_topup_picker = true;
        if (missionTopupId) body.mission_topup_id = missionTopupId;
      }
      const res = await axios.post(`${API}/payments/checkout/subscription`, body);
      if (res.data?.url) {
        await openStripeCheckout(res.data.url);
      }
    } catch (e) {
      console.error('Checkout failed', e);
      setLoading(null);
      setTopupModalPlan(null);
      alert('Checkout failed. Please try again.');
      throw e;
    }
  };

  const confirmTopupAndSubscribe = async (missionTopupId) => {
    if (!topupModalPlan) return;
    await subscribe(topupModalPlan.id, missionTopupId);
  };

  const becomeFounder = () => {
    if (!user) {
      window.location.href = `/login?return=/pricing`;
      return;
    }
    // Open the pre-checkout interstitial so the user can review the
    // sub-stacking choices (merch credit vs forfeit) before Stripe.
    // No-active-sub users get a streamlined confirm screen inside the
    // same modal.
    setFounderModalOpen(true);
  };

  const confirmFounderCheckout = async (merchCreditChoice) => {
    setLoading('founder');
    try {
      const res = await axios.post(`${API}/payments/checkout/founder`, {
        origin_url: window.location.origin,
        merch_credit_choice: merchCreditChoice,
      });
      if (res.data?.url) {
        await openStripeCheckout(res.data.url);
      }
    } catch (e) {
      setLoading(null);
      const code = e?.response?.data?.detail?.code;
      if (code === 'already_founder') {
        alert('You are already a Founder on this account.');
        setFounderModalOpen(false);
      } else if (code === 'founder_seats_sold_out') {
        alert('All Founder seats have been claimed.');
        setFounderModalOpen(false);
      } else if (code === 'merch_credit_choice_required') {
        // The modal already handles this — just re-surface the error.
        throw e;
      } else {
        throw e;
      }
    }
  };

  return (
    <div className="min-h-screen bg-transparent text-enf-text-primary px-4 py-10 sm:py-16" data-testid="pricing-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-enf-text-muted hover:text-enf-text-primary transition-colors mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <span className="font-display text-4xl tracking-wide">Enferno</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl tracking-wide mb-3">Intelligent Adaptive Wellness</h1>
        <p className="text-enf-text-secondary text-base mb-10 max-w-2xl">
          Strength. Endurance. Combatives. Recovery. Nutrition. For Every Discipline, Every Day, EveryBody &mdash; adaptive users from day one, not as an afterthought.
        </p>

        {canceled && (
          <div className="mb-6 p-3 rounded-md bg-amber-900/30 border border-amber-700/40 text-amber-200 text-sm" data-testid="pricing-canceled-banner">
            Checkout canceled. No charge made. Pick a plan when you're ready.
          </div>
        )}

        {/* Manage billing — visible ONLY for active paid subs. The single
            place users go to cancel, switch billing card, change between
            monthly/annual, or get receipts. Profile's PlanAndBillingBlock
            routes here for all sub management. */}
        {hasActivePaidSub && (
          <div className="mb-6 p-4 rounded-xl border bg-enf-surface-deep border-enf-surface-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="pricing-manage-billing">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Your current plan</div>
              <div className="text-sm text-enf-text-secondary">
                Enferno {mySub?.plan_id === 'enferno-annual' ? '(Annual)' : '(Monthly)'} — manage billing, switch plans, or cancel in the Stripe portal.
              </div>
              {portalError && (
                <p className="text-xs text-red-400 mt-1" data-testid="pricing-portal-error">{portalError}</p>
              )}
            </div>
            <button
              type="button"
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="pricing-manage-billing-button"
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Manage billing
            </button>
          </div>
        )}

        {/* Soft "step away" inline text for lifetime grantees (Founder /
            Hero / Comp). $249 lifetime is permanent — there's no
            self-serve downgrade. Surfaces a quiet contact path for the
            rare user who genuinely wants out. NOT a CTA. */}
        {myLifetime && (
          <div className="mb-6 px-4 py-3 rounded-md border bg-amber-900/10 border-amber-700/30" data-testid="pricing-lifetime-stepaway">
            <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-1">
              {myTier === 'founder' ? 'You are a Founder' : myTier === 'hero' ? 'You are a Hero' : 'Lifetime access'}
            </div>
            <p className="text-sm text-enf-text-secondary">
              You have lifetime Enferno access. Nothing to manage here.
              Need to step away? Email <a href="mailto:support@enferno.app" className="text-enf-accent-fg underline">support@enferno.app</a>.
            </p>
          </div>
        )}

        {/* Founder's tier — at the top so high-price + limited-supply is the
            first thing visitors see. Compact horizontal layout. */}
        <div
          id="founder"
          className="mb-8 p-5 rounded-xl border bg-gradient-to-br from-amber-900/25 to-zinc-950 border-amber-700/40"
          data-testid="tier-founder"
        >
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Founder's tier · limited</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-900/40 border border-amber-700/40 text-amber-300" data-testid="tier-founder-seats">
                  {founderSeats
                    ? (founderSeats.remaining > 0
                        ? `${founderSeats.remaining} of ${founderSeats.total} remaining`
                        : 'Sold out')
                    : '500 slots'}
                </span>
              </div>
              <div className="font-display text-2xl sm:text-3xl tracking-wide text-white mb-1">$249 &mdash; lifetime access</div>
              <p className="text-xs sm:text-sm text-zinc-300">
                One-time payment. Lifetime Enferno access. Numbered tee + sticker pack mailed at launch. Donors Wall recognition. Backs Enferno + IronLimb directly.
              </p>
            </div>
            <button
              type="button"
              onClick={becomeFounder}
              disabled={
                loading === 'founder'
                || (founderSeats && !founderSeats.available)
                || myLifetime
              }
              data-testid="tier-founder-cta"
              className={`px-5 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors whitespace-nowrap ${
                (founderSeats && !founderSeats.available) || myLifetime
                  ? 'bg-amber-900/40 border border-amber-700/40 text-amber-200 cursor-not-allowed'
                  : 'bg-amber-600 hover:bg-amber-500 text-zinc-950'
              }`}
            >
              {loading === 'founder' && <Loader2 className="w-3 h-3 animate-spin" />}
              {myLifetime
                ? (myTier === 'founder' ? 'Your current plan' : 'You have Lifetime')
                : (founderSeats && !founderSeats.available
                    ? 'Founder seats sold out'
                    : (loading === 'founder' ? 'Opening checkout…' : 'Become a Founder'))}
            </button>
          </div>
        </div>

        {/* Two tier cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          {/* Egnite */}
          <div className="p-6 rounded-xl border bg-enf-surface-deep border-enf-surface-border" data-testid="tier-egnite">
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Egnite</div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="font-display text-4xl tracking-wide">Free</span>
              <span className="text-xs text-enf-text-muted">forever</span>
            </div>
            <p className="text-sm text-enf-text-secondary mb-5">For everybody. No trial. No credit card. Just train.</p>
            <ul className="space-y-2 mb-5">
              {EGNITE_PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-enf-text-secondary">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>

            {/* G6: "What you're missing" — honest, anti-shame
                disclosure of Egnite limits. Not a guilt-trip; just makes
                the apples-to-apples comparison legible. */}
            <div className="mb-6 pt-4 border-t border-enf-surface-border">
              <div className="text-[11px] uppercase tracking-[0.25em] text-enf-text-muted font-semibold mb-3">
                What you're missing
              </div>
              <ul className="space-y-2">
                {EGNITE_LIMITS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-xs text-enf-text-muted leading-relaxed">
                    <Lock className="w-3 h-3 text-enf-text-muted flex-shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>

            {/* Egnite CTA — three states:
                  - Signed-out visitor: "Get started — free" → /register
                  - Signed-in Egnite user: "Your current plan" (disabled)
                  - Signed-in higher-tier user: "You have a higher tier"
                    (disabled, soft) */}
            {!user ? (
              <Link
                to="/register"
                data-testid="tier-egnite-cta"
                className="block w-full text-center px-4 py-2.5 rounded-md text-sm font-semibold bg-enf-surface-mid text-enf-text-primary hover:bg-enf-surface-elevated transition-colors"
              >
                Get started — free
              </Link>
            ) : (
              <div
                data-testid="tier-egnite-cta"
                className="block w-full text-center px-4 py-2.5 rounded-md text-sm font-semibold bg-enf-surface-deep border border-enf-surface-border text-enf-text-muted cursor-not-allowed"
              >
                {myTier === 'egnite' && !myLifetime ? 'Your current plan' : 'You have a higher tier'}
              </div>
            )}
          </div>

          {/* Enferno */}
          <div className="p-6 rounded-xl border bg-gradient-to-br from-enf-accent/10 to-zinc-950 border-enf-accent-fg/50 relative" data-testid="tier-enferno">
            <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Enferno — fully envolved</div>
            <div className="flex items-baseline gap-2 mb-2 flex-wrap">
              {PLANS.map((p) => (
                <div key={p.id} className="flex items-baseline gap-1">
                  <span className={`font-display ${p.highlight ? 'text-4xl' : 'text-2xl text-enf-text-secondary'} tracking-wide`}>{p.price}</span>
                  <span className="text-xs text-enf-text-muted mr-2">{p.cadence}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-enf-text-secondary mb-5">Cancel anytime. Annual saves 42%.</p>

            <ul className="space-y-2 mb-6 max-h-[400px] overflow-y-auto">
              {ENFERNO_PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-enf-text-secondary">
                  <Check className="w-4 h-4 text-enf-accent-fg flex-shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              {PLANS.map((p) => {
                const isCurrentPlan = hasActivePaidSub && mySub?.plan_id === p.id;
                const isOtherEnfernoPlan = hasActivePaidSub && mySub?.plan_id !== p.id;
                const blockedByLifetime = myLifetime;
                const disabled = loading === p.id || isCurrentPlan || isOtherEnfernoPlan || blockedByLifetime;
                const label = (() => {
                  if (loading === p.id) return null;
                  if (isCurrentPlan) return 'Your current plan';
                  if (isOtherEnfernoPlan) return 'Switch in Manage billing';
                  if (blockedByLifetime) return 'You have Lifetime';
                  return `Subscribe — ${p.label} ${p.price}${p.cadence}`;
                })();
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => startSubscribe(p.id)}
                    disabled={disabled}
                    data-testid={`subscribe-${p.id}`}
                    className={`w-full px-4 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2 ${
                      disabled
                        ? 'bg-enf-surface-deep border border-enf-surface-border text-enf-text-muted cursor-not-allowed'
                        : (p.highlight
                            ? 'bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary'
                            : 'bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-primary border border-enf-surface-border')
                    }`}
                  >
                    {loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Top-up donations strip */}
        <div className="p-5 rounded-xl border bg-enf-surface-deep border-enf-surface-border mb-10" data-testid="topup-strip">
          <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Optional · mission top-up</div>
          <div className="font-display text-2xl tracking-wide mb-1">Add a contribution to IronLimb</div>
          <p className="text-sm text-enf-text-secondary mb-4">
            {devTopupPicker
              ? 'When you subscribe, you\'ll choose whether to add a recurring IronLimb top-up before checkout. One-time gifts below — 100% routes where you pick.'
              : 'When you subscribe, Stripe Checkout lets you add a recurring top-up (+$2 research · +$2 sponsor an amputee user · +$4 both) billed with your plan. One-time gifts below — 100% routes where you pick.'}
          </p>
          <button
            type="button"
            onClick={() => setDonateOpen(true)}
            data-testid="topup-donate-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary transition-colors mb-3"
          >
            <Heart className="w-4 h-4" /> Donate now
          </button>
          <p className="text-xs text-enf-text-muted">
            Not yet tax-deductible — IronLimb Foundation's 501(c)(3) is pending. Top-ups after the grant date will be tax-deductible and we'll notify all contributors by email with receipts.
          </p>
        </div>

        {/* Founder's Standard */}
        {/* Founder's tier card was here — moved to top of page so
            high-price + limited-supply is first thing visitors see. */}


        {/* Competitor comparison */}
        <div className="mb-10" data-testid="competitor-comparison">
          <h2 className="font-display text-2xl tracking-wide mb-4">For comparison</h2>
          <div className="border border-enf-surface-border rounded-lg overflow-hidden">
            {COMPETITORS.map((c, i) => (
              <div
                key={c.name}
                className={`flex items-center justify-between px-4 py-3 ${i > 0 ? 'border-t border-enf-surface-border' : ''} ${c.highlight ? 'bg-enf-accent/10' : ''}`}
              >
                <span className={`text-sm ${c.highlight ? 'font-semibold text-enf-text-primary' : 'text-enf-text-secondary'}`}>{c.name}</span>
                <span className={`text-sm font-mono ${c.highlight ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`}>{c.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-enf-text-muted mt-2">Prices as of mid-2026. Enferno gives you all 5 pillars in one app — Strava only covers endurance, MyFitnessPal only covers nutrition.</p>
        </div>

        {/* FAQ */}
        <div data-testid="pricing-faq">
          <h2 className="font-display text-2xl tracking-wide mb-4">FAQ</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I cancel anytime?', a: 'Yes. Cancel in the Customer Portal — full refund within 14 days of any new charge.' },
              { q: 'What happens to my data if I downgrade?', a: 'Nothing is deleted. Your full history is preserved; you just see the most recent 30 days while on Egnite. Resubscribe and everything is back.' },
              { q: 'Where does my money go?', a: 'Subscription revenue funds Enferno operations (hosting, AI compute, dev). Optional top-up contributions route entirely to IronLimb Foundation. Founder\'s-tier payments support both.' },
              { q: 'Why Enferno over a "free" app?', a: '"Free" apps fund themselves with ads, data resale, or shallow features. Enferno funds itself with subscriptions — your data stays yours, your training stays in the foreground, your mission stays the point.' },
            ].map((f) => (
              <div key={f.q} className="p-4 rounded-lg bg-enf-surface-deep border border-enf-surface-border">
                <div className="font-semibold text-sm text-enf-text-primary mb-1">{f.q}</div>
                <div className="text-sm text-enf-text-secondary">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <DonationModal open={donateOpen} onOpenChange={setDonateOpen} />
      {founderModalOpen && (
        <FounderUpgradeDialog
          onClose={() => { setFounderModalOpen(false); setLoading(null); }}
          onConfirm={confirmFounderCheckout}
        />
      )}
      {topupModalPlan && (
        <MissionTopupCheckoutDialog
          plan={topupModalPlan}
          onClose={() => { setTopupModalPlan(null); setLoading(null); }}
          onConfirm={confirmTopupAndSubscribe}
        />
      )}
    </div>
  );
};

export default Pricing;
